import React, { FC, useState, useContext, useMemo, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';

import { Button, CircularProgress, Grid, Typography, Box } from '@mui/material';

import AcceptanceConfirmationModal from '../../../sharedModules/layout/AcceptanceConfirmationModal';
import OfflineWarningAlert from './OfflineWarningAlert';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import ShakingAlert, { ShakingAlertHandle } from '../../../sharedModules/layout/ShakingAlert';

import { backflowLogRequiredFields } from '../../backflowJobLists/constants/requiredFields';
import validateRequiredFields from '../../../utils/validateRequiredFields';

import { BackflowLog, BackflowJobItem } from '../../model';

import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';
import { persistLog, markLogSynced } from '../../utils/backflowCache';

interface BackflowLogSubmitButtonsProps {
  backflowJobItem: BackflowJobItem;
  formValues: BackflowLog;
  outOfService: boolean;
  fetchBackflowJobItem: () => void;
  requiredDeviceImageAttached?: boolean;
  requiredDataplateAttached?: boolean;
  onRequiredImagesValidationFailed?: () => void;
  onSavedOffline?: () => void;
}

const BackflowLogSubmitButtons: FC<BackflowLogSubmitButtonsProps> = (props) => {
  const { formAction } = useParams<{ formAction?: string }>();
  const location = useLocation();

  const approveMode = formAction === 'approve';

  const deviceAcctShakingAlertRef = useRef<ShakingAlertHandle>(null);

  const history = useHistory();

  const {
    backflowJobItem,
    formValues,
    outOfService,
    fetchBackflowJobItem,
    requiredDeviceImageAttached = true,
    requiredDataplateAttached = true,
    onRequiredImagesValidationFailed,
    onSavedOffline,
  } = props;

  // Get backLink and offlineOnly from URL params
  const searchParams = new URLSearchParams(location.search);
  const backLinkParam = searchParams.get('backLink');
  const offlineOnly = searchParams.get('offlineOnly') === 'true';

  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [acceptanceConfirmationModalOpen, setAcceptanceConfirmationModalOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<string>();
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);

  // If using the full form, many fields are required.
  // If using the comment-only form, only the comment is required.
  const requiredFields = outOfService
    ? [{ key: 'internal_comment', label: 'Comment' }]
    : backflowLogRequiredFields(backflowJobItem.backflow_type);

  useEffect(() => {
    const updateOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  const submitStatus = () => {
    if (outOfService) return 'out_of_service';
    if (approveMode) return 'approved';
    return 'pending_approval';
  };

  const saveToLocalStorage = () => {
    try {
      setLoading(true);
      onSavedOffline?.();

      const persisted = persistLog(
        backflowJobItem?.backflow_job_group_id,
        backflowJobItem?.id,
        formValues,
        outOfService,
      );

      if (!persisted) {
        setErrorMessage('Job group data not cached. Please visit the job group page while online first.');
        setLoading(false);
        return;
      }

      setSnackBarMessage('Form saved to your device. You can submit it when you have connectivity.');
      handleSnackbarAlertSeverityChange('info');
      setSnackBarOpen(true);
      setErrorMessage('');

      // Redirect after a short delay
      const jobGroupId = backflowJobItem?.backflow_job_group_id;
      let redirectUrl = '';
      if (backLinkParam) {
        redirectUrl = backLinkParam;
      } else if (offlineOnly) {
        // If in offline-only mode, redirect to unsubmitted tests page
        redirectUrl = '/backflow_tests/unsubmitted';
      } else {
        redirectUrl = `/backflow_tests/backflow_job_groups/${jobGroupId}`;
      }

      setTimeout(() => {
        history.push(redirectUrl);
      }, 2000);
    } catch (error) {
      setErrorMessage('Failed to save to device. Please try again.');
      console.error('Error saving to localStorage:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitJobItem = async () => {
    if (loading) return; // guard double-taps, including modal confirm

    try {
      setLoading(true);

      // Save to localStorage before the API call so data is recoverable if the request fails
      persistLog(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id, formValues, outOfService);

      await axios.put(`/backflows/backflow_logs/${backflowJobItem.id}`, {
        ...formValues,
        status: submitStatus(),
      });

      // Mark the backflow_log as synced in localStorage after successful submission
      markLogSynced(backflowJobItem?.backflow_job_group_id, backflowJobItem?.id);
      // Refetch the job item to get the updated completed_at value
      await fetchBackflowJobItem();

      setSnackBarMessage('Backflow test form submitted successfully!');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);
      setErrorMessage('');

      // Redirect after a short delay
      let redirectUrl = '';

      // If there's a backLink parameter, use that
      if (backLinkParam) {
        redirectUrl = backLinkParam;
      } else if (offlineOnly) {
        // If in offline-only mode, redirect to unsubmitted tests page
        redirectUrl = '/backflow_tests/unsubmitted';
      } else {
        // Otherwise, default to job group page
        let urlSuffix = '';
        if (approveMode) urlSuffix = '/approve';
        redirectUrl = `/backflow_tests/backflow_job_groups/${backflowJobItem?.backflow_job_group_id}${urlSuffix}`;
      }

      setTimeout(() => {
        history.push(redirectUrl);
      }, 2000);
    } catch (error) {
      setErrorMessage('Failed to submit form. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmitClicked = async () => {
    setHasAttemptedSubmit(true);

    if (!outOfService && backflowJobItem?.missing_fields?.length > 0) return;

    const validate = validateRequiredFields(formValues, requiredFields);
    if (!validate.isValid) return;

    if (approveMode && backflowJobItem?.requires_backflow_device_account_number && !backflowJobItem?.u_bfp_acct) {
      deviceAcctShakingAlertRef.current?.shake();
      return;
    }

    if (!outOfService && (!requiredDeviceImageAttached || !requiredDataplateAttached)) {
      onRequiredImagesValidationFailed?.();
      return;
    }

    if (approveMode && backflowJobItem.is_final_approval_item) {
      setAcceptanceConfirmationModalOpen(true);
    } else {
      submitJobItem();
    }
  };

  const confirmMessage = (
    <p>
      This will approve the final Backflow Device for this work order. Doing so will notify all Tudi employees who
      receive approval notifications and submit the updated device details to Servman. This cannot be undone.
    </p>
  );

  const buttonToShow = () => {
    if (loading) return <CircularProgress size={24} />;
    if (backflowJobItem?.status === 'approved')
      return (
        <Button variant="contained" color="success" size="large" disabled>
          Approved
        </Button>
      );
    if (!isOnline)
      return (
        <Button variant="contained" color="warning" size="large" onClick={saveToLocalStorage}>
          Save to your Device
        </Button>
      );
    if (approveMode)
      return (
        <Button variant="contained" color="primary" size="large" onClick={handleSubmitClicked}>
          Approve
        </Button>
      );
    return (
      <Button variant="contained" color="primary" size="large" onClick={handleSubmitClicked}>
        Submit for Approval
      </Button>
    );
  };

  // Compute incomplete fields in real-time for the checklist
  const incompleteItems = useMemo(() => {
    if (backflowJobItem?.status === 'approved') return [];

    const items: string[] = [];

    // Missing device details from backend (not applicable for out-of-service items)
    if (!outOfService && backflowJobItem?.missing_fields?.length > 0) {
      backflowJobItem.missing_fields.forEach((field) => items.push(field));
    }

    // Missing form fields
    const validation = validateRequiredFields(formValues, requiredFields);
    validation.missingLabels.forEach((label) => items.push(label));

    // Missing required images
    if (!outOfService) {
      if (!requiredDeviceImageAttached) {
        items.push('Backflow Device Image');
      }
      if (!requiredDataplateAttached) {
        items.push('Capture Backflow Dataplate');
      }
    }

    if (approveMode && backflowJobItem?.requires_backflow_device_account_number && !backflowJobItem?.u_bfp_acct) {
      items.push('Device Account Number');
    }

    return items;
  }, [
    formValues,
    requiredFields,
    backflowJobItem,
    outOfService,
    requiredDeviceImageAttached,
    requiredDataplateAttached,
  ]);

  const isFormIncomplete = incompleteItems.length > 0 && backflowJobItem?.status !== 'approved';

  useEffect(() => {
    if (isFormIncomplete) {
      window.onbeforeunload = () => 'This form is not complete. Are you sure you want to leave?';
    }
    return () => {
      window.onbeforeunload = null;
    };
  }, [isFormIncomplete]);

  return (
    <Grid container>
      <AcceptanceConfirmationModal
        onConfirm={submitJobItem}
        message={confirmMessage}
        modalOpen={acceptanceConfirmationModalOpen}
        setModalOpen={setAcceptanceConfirmationModalOpen}
        title="Approve Final Device for this Work Order"
      />
      <Grid item xs={12}>
        <OfflineWarningAlert isOnline={isOnline} />
        <ErrorMessage message={errorMessage} />
      </Grid>
      {approveMode && backflowJobItem?.requires_backflow_device_account_number && !backflowJobItem?.u_bfp_acct && (
        <Grid item xs={12} sx={{ mb: 1 }}>
          <ShakingAlert
            ref={deviceAcctShakingAlertRef}
            message="Device Account Number is required before approving."
            severity="warning"
          />
        </Grid>
      )}
      <Grid item xs={12}>
        {buttonToShow()}
      </Grid>
      {hasAttemptedSubmit && isFormIncomplete && (
        <Grid item xs={12}>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Required fields not yet completed:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {incompleteItems.map((item) => (
                <Typography component="li" variant="body2" color="text.secondary" key={item} sx={{ py: 0.25 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default BackflowLogSubmitButtons;
